import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useBidSocket } from '../hooks/useBidSocket';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import PayPalCheckout from '../components/PayPalCheckout';
import BidPayPalCheckout from '../components/BidPayPalCheckout';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Share2, 
  Heart,
  Ticket,
  TrendingUp,
  Gavel,
  CheckCircle,
  Minus,
  Plus,
  Wifi,
  WifiOff,
  Tag,
  CreditCard
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sample event for demo
const sampleEvent = {
  id: '1',
  title: 'Gaming Stream Night with AlexGamer',
  description: 'Join AlexGamer for an epic gaming stream night! This exclusive event includes live gameplay, Q&A sessions, exclusive giveaways, and a chance to play alongside your favorite streamer. Limited spots available for this once-in-a-lifetime experience.\n\nWhat to expect:\n- 3 hours of live gaming\n- Exclusive Q&A session\n- Gaming merchandise giveaway\n- VIP viewers get to play with AlexGamer',
  influencer_name: 'AlexGamer',
  influencer_avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
  category: 'gaming',
  event_type: 'virtual',
  event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  location: 'Virtual Event - Twitch',
  image_url: 'https://customer-assets.emergentagent.com/job_creator-signup-4/artifacts/7bii36vj_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2008_44_35%20PM.png',
  total_tickets: 100,
  sold_tickets: 78,
  ticket_tiers: [
    { id: '1', name: 'General Access', description: 'Watch the stream and participate in chat', price: 25, quantity: 80, sold: 65, sale_mode: 'fixed' },
    { id: '2', name: 'VIP Access', description: 'Play with AlexGamer + Exclusive merch', price: 100, quantity: 20, sold: 13, sale_mode: 'auction', min_bid: 75 }
  ],
  status: 'published'
};

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [bidAmount, setBidAmount] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [bids, setBids] = useState([]);
  const [auctionTierInfo, setAuctionTierInfo] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [showBidPayPal, setShowBidPayPal] = useState(false);
  const [pendingBidOrderId, setPendingBidOrderId] = useState(null);
  const [pendingBidId, setPendingBidId] = useState(null);

  // WebSocket for real-time bid updates
  const handleNewBid = useCallback((bidData) => {
    toast.info(`New bid: $${bidData.amount} by ${bidData.bidder_name}`, {
      icon: <Gavel className="w-4 h-4" />,
      duration: 4000
    });
    
    // Update local bids state
    setBids(prevBids => {
      // Mark old winning bids as outbid
      const updated = prevBids.map(b => 
        b.tier_name === bidData.tier_name && b.status === 'winning' 
          ? { ...b, status: 'outbid' } 
          : b
      );
      // Add the new bid
      return [{
        id: bidData.bid_id,
        tier_name: bidData.tier_name,
        amount: bidData.amount,
        fan_name: bidData.bidder_name,
        status: 'winning',
        created_at: bidData.timestamp
      }, ...updated];
    });
  }, []);

  const { isConnected: wsConnected } = useBidSocket(eventId, handleNewBid);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
      
      // Fetch bids for auction tiers
      const bidsRes = await axios.get(`${API_URL}/events/${eventId}/bids`);
      setBids(bidsRes.data.bids || []);
      // Store tier info for auction slots
      if (bidsRes.data.tier_info) {
        setAuctionTierInfo(bidsRes.data.tier_info);
      }
      
      // Check if user has liked this event
      if (isAuthenticated) {
        try {
          const likedRes = await axios.get(`${API_URL}/events/${eventId}/liked`);
          setIsLiked(likedRes.data.liked);
        } catch (e) {
          // Ignore error if not logged in
        }
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = (tier) => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase tickets');
      navigate('/login');
      return;
    }
    setSelectedTier(tier);
    setQuantity(1);
    if (tier.sale_mode === 'auction') {
      // Get tier info for multi-slot auction
      const tierInfo = auctionTierInfo[tier.name];
      if (tierInfo && tierInfo.filled_slots >= tierInfo.total_slots && tierInfo.lowest_winning_bid) {
        // All slots filled - need to beat lowest winning bid
        setBidAmount((tierInfo.lowest_winning_bid + 5).toString());
      } else {
        // Open slots available - start at minimum bid
        setBidAmount((tier.min_bid || tier.price).toString());
      }
    }
    setShowPurchaseModal(true);
    setShowPayPal(false);
    setPromoCode('');
    setPromoApplied(null);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const subtotal = selectedTier.price * quantity;
      const res = await axios.post(`${API_URL}/promo-codes/validate`, {
        code: promoCode,
        event_id: eventId,
        subtotal: subtotal
      });
      setPromoApplied(res.data);
      toast.success(`Promo code applied! ${res.data.discount_type === 'percentage' ? res.data.discount_value + '% off' : '$' + res.data.discount_value + ' off'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoApplied(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedTier) return;
    
    // For auction bids, create a PayPal order for the bid
    if (selectedTier.sale_mode === 'auction') {
      const amount = parseFloat(bidAmount);
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid bid amount');
        return;
      }
      
      // Check minimum bid
      if (selectedTier.min_bid && amount < selectedTier.min_bid) {
        toast.error(`Minimum bid is $${selectedTier.min_bid}`);
        return;
      }
      
      // Check against lowest winning bid if all slots are filled
      const tierInfo = auctionTierInfo[selectedTier.name];
      if (tierInfo && tierInfo.filled_slots >= tierInfo.total_slots && tierInfo.lowest_winning_bid) {
        if (amount <= tierInfo.lowest_winning_bid) {
          toast.error(`All ${tierInfo.total_slots} spots are filled. Bid must be higher than $${tierInfo.lowest_winning_bid.toFixed(2)}`);
          return;
        }
      }
      
      setPurchasing(true);
      try {
        // Create a PayPal order for the bid
        const res = await axios.post(`${API_URL}/bids/create-order`, {
          event_id: eventId,
          tier_name: selectedTier.name,
          amount: amount
        });
        
        setPendingBidOrderId(res.data.paypal_order_id);
        setPendingBidId(res.data.bid_id);
        setShowBidPayPal(true);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create bid');
      } finally {
        setPurchasing(false);
      }
      return;
    }
    
    // For fixed price tickets, show PayPal
    setShowPayPal(true);
  };

  const handleBidPayPalApprove = async (data) => {
    try {
      const res = await axios.post(`${API_URL}/bids/confirm-payment`, {
        orderID: data.orderID,
        bid_id: pendingBidId
      });
      
      toast.success(res.data.message || 'Bid placed successfully! You are the highest bidder.');
      setShowBidPayPal(false);
      setShowPurchaseModal(false);
      setPendingBidOrderId(null);
      setPendingBidId(null);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm bid payment');
      setShowBidPayPal(false);
    }
  };

  const handleBidPayPalError = (error) => {
    toast.error('Payment failed. Please try again.');
    setShowBidPayPal(false);
    setPendingBidOrderId(null);
    setPendingBidId(null);
  };

  const handlePayPalSuccess = (data) => {
    setShowPurchaseModal(false);
    setShowPayPal(false);
    fetchEvent();
    navigate('/my-tickets');
  };

  const handlePayPalCancel = () => {
    setShowPayPal(false);
  };

  const handlePayPalError = (error) => {
    console.error('PayPal checkout error:', error);
    setShowPayPal(false);
    // Error toast is already shown by PayPalCheckout component
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: event?.title || 'Check out this event!',
      text: `Check out "${event?.title}" on FanBound!`,
      url: shareUrl
    };

    // Try native share first (mobile), then clipboard
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
        return;
      } catch (error) {
        if (error.name === 'AbortError') return; // User cancelled
        // Fall through to clipboard
      }
    }
    
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch {
      // Final fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save events');
      navigate('/login');
      return;
    }
    
    try {
      if (isLiked) {
        await axios.delete(`${API_URL}/events/${eventId}/like`);
        setIsLiked(false);
        toast.info('Event removed from favorites');
      } else {
        await axios.post(`${API_URL}/events/${eventId}/like`);
        setIsLiked(true);
        toast.success('Event saved to your favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="aspect-[2/1] bg-white/10 rounded-xl" />
            <div className="h-8 bg-white/10 rounded w-3/4" />
            <div className="h-4 bg-white/10 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Event Not Found</h2>
          <p className="text-muted-foreground mb-4">This event may have been removed or doesn't exist.</p>
          <Button onClick={() => navigate('/events')}>Browse Events</Button>
        </div>
      </div>
    );
  }

  const availableTickets = event.total_tickets - event.sold_tickets;
  const isSoldOut = availableTickets === 0;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Image */}
        <div className="relative aspect-[2/1] rounded-xl overflow-hidden mb-8">
          <img 
            src={event.image_url || 'https://customer-assets.emergentagent.com/job_creator-signup-4/artifacts/7bii36vj_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2008_44_35%20PM.png'}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            <Badge className="capitalize bg-primary/90">{event.category}</Badge>
            {event.event_type === 'virtual' && (
              <Badge variant="secondary">Virtual</Badge>
            )}
          </div>

          {/* Share/Save */}
          <div className="absolute top-4 right-4 flex gap-2">
            <Button 
              size="icon" 
              variant="secondary" 
              className="rounded-full"
              onClick={handleShare}
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="secondary" 
              className={`rounded-full ${isLiked ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
              onClick={handleLike}
              data-testid="like-button"
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Info */}
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-4" data-testid="event-title">
                {event.title}
              </h1>
              
              <div 
                className="flex items-center gap-4 mb-6 cursor-pointer group"
                onClick={() => navigate(`/creator/${event.influencer_id}`)}
              >
                <img 
                  src={event.influencer_avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop'}
                  alt={event.influencer_name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-primary group-hover:border-primary/80 transition-colors"
                />
                <div>
                  <p className="font-medium">Hosted by</p>
                  <p className="text-primary font-semibold group-hover:text-primary/80 transition-colors">{event.influencer_name}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-6 text-muted-foreground">
                {event.event_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span>{format(new Date(event.event_date), 'EEEE, MMMM d, yyyy')}</span>
                  </div>
                )}
                {event.event_date && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <span>{format(new Date(event.event_date), 'h:mm a')}</span>
                  </div>
                )}
                {event.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-card border border-white/10 rounded-xl p-6">
              <h2 className="font-heading font-semibold text-xl mb-4">About This Event</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-muted-foreground whitespace-pre-line">{event.description}</p>
              </div>
            </div>

            {/* Ticket Tiers */}
            <div>
              <h2 className="font-heading font-semibold text-xl mb-4">Choose Your Ticket</h2>
              <div className="space-y-4">
                {event.ticket_tiers?.map((tier) => {
                  const tierAvailable = tier.quantity - (tier.sold || 0);
                  const tierSoldOut = tierAvailable <= 0;
                  const isAuction = tier.sale_mode === 'auction';
                  const tierInfo = auctionTierInfo[tier.name];
                  const winningBids = bids.filter(b => b.tier_name === tier.name && b.status === 'winning');
                  const highestBid = winningBids[0];
                  const lowestWinningBid = winningBids.length > 0 ? winningBids[winningBids.length - 1] : null;

                  return (
                    <div 
                      key={tier.id || tier.name}
                      className={`border rounded-xl p-6 transition-all ${
                        tierSoldOut && !isAuction
                          ? 'border-white/5 bg-white/5 opacity-60' 
                          : 'border-white/10 bg-white/5 hover:border-primary/50 cursor-pointer'
                      }`}
                      onClick={() => !(tierSoldOut && !isAuction) && handleSelectTier(tier)}
                      data-testid={`ticket-tier-${tier.name}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-heading font-semibold text-lg">{tier.name}</h3>
                            {isAuction && (
                              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 gap-1">
                                <Gavel className="w-3 h-3" />
                                Auction
                              </Badge>
                            )}
                            {tierSoldOut && !isAuction && <Badge variant="destructive">Sold Out</Badge>}
                          </div>
                          {tier.description && (
                            <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {isAuction ? (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {tierInfo ? `${tierInfo.filled_slots}/${tierInfo.total_slots} spots filled` : `${tier.quantity} spots`}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {tierAvailable} left
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {isAuction ? (
                            <div>
                              {tierInfo && tierInfo.filled_slots >= tierInfo.total_slots ? (
                                <>
                                  <p className="text-xs text-muted-foreground">Minimum to enter</p>
                                  <p className="font-heading font-bold text-2xl text-primary">
                                    ${lowestWinningBid ? (lowestWinningBid.amount + 1).toFixed(0) : tier.min_bid || tier.price}+
                                  </p>
                                  <p className="text-xs text-amber-400">Outbids lowest winner</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-xs text-muted-foreground">Starting bid</p>
                                  <p className="font-heading font-bold text-2xl text-primary">
                                    ${tier.min_bid || tier.price}
                                  </p>
                                  <p className="text-xs text-green-400">Open spots available</p>
                                </>
                              )}
                            </div>
                          ) : (
                            <p className="font-heading font-bold text-2xl text-primary">${tier.price}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Purchase Card */}
            <div className="sticky top-24 bg-card border border-white/10 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Availability</span>
                <Badge variant={isSoldOut ? 'destructive' : 'secondary'}>
                  {isSoldOut ? 'Sold Out' : `${availableTickets} spots left`}
                </Badge>
              </div>

              {event.ticket_tiers?.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Starting from</span>
                  <span className="font-heading font-bold text-2xl text-primary">
                    ${Math.min(...event.ticket_tiers.map(t => t.price))}
                  </span>
                </div>
              )}

              <div className="pt-4 border-t border-white/10">
                {!isSoldOut && event.ticket_tiers?.length > 0 && (
                  <Button 
                    className="w-full gap-2" 
                    size="lg"
                    onClick={() => handleSelectTier(event.ticket_tiers[0])}
                    data-testid="get-tickets-btn"
                  >
                    <Ticket className="w-5 h-5" />
                    Get Tickets
                  </Button>
                )}
                {isSoldOut && (
                  <Button className="w-full" size="lg" disabled>
                    Sold Out
                  </Button>
                )}
              </div>

              {/* Social Proof */}
              {event.sold_tickets > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span>{event.sold_tickets} people attending</span>
                </div>
              )}
              
              {/* Live Bid Status */}
              {event.ticket_tiers?.some(t => t.sale_mode === 'auction') && (
                <div className={`flex items-center gap-2 text-sm pt-2 ${wsConnected ? 'text-green-400' : 'text-muted-foreground'}`}>
                  {wsConnected ? (
                    <>
                      <Wifi className="w-4 h-4" />
                      <span>Live bid updates active</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-4 h-4" />
                      <span>Connecting...</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedTier?.sale_mode === 'auction' ? 'Place Your Bid' : 'Purchase Tickets'}
            </DialogTitle>
            <DialogDescription>
              {selectedTier?.name} - {event?.title}
            </DialogDescription>
          </DialogHeader>

          {selectedTier && (
            <div className="space-y-6 py-4">
              {selectedTier.sale_mode === 'auction' ? (
                <div className="space-y-4">
                  {/* Show auction slot status */}
                  {(() => {
                    const tierInfo = auctionTierInfo[selectedTier.name];
                    const isFull = tierInfo && tierInfo.filled_slots >= tierInfo.total_slots;
                    return (
                      <div className={`p-4 rounded-lg ${isFull ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium">{tierInfo ? `${tierInfo.filled_slots}/${tierInfo.total_slots}` : selectedTier.quantity} spots</p>
                          {isFull ? (
                            <span className="text-xs text-amber-400">All spots filled</span>
                          ) : (
                            <span className="text-xs text-green-400">Open spots available</span>
                          )}
                        </div>
                        {isFull && tierInfo?.lowest_winning_bid ? (
                          <p className="text-sm text-muted-foreground">
                            Bid must exceed ${tierInfo.lowest_winning_bid.toFixed(2)} to enter
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Minimum bid: ${selectedTier.min_bid || selectedTier.price}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Your Bid Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        className="pl-8"
                        min={selectedTier.min_bid || selectedTier.price}
                        data-testid="bid-amount-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Your payment will be captured immediately. If outbid, you'll be automatically refunded.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Price per ticket</span>
                    <span className="font-semibold">${selectedTier.price}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Quantity</span>
                    <div className="flex items-center gap-3">
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => {
                          setQuantity(Math.max(1, quantity - 1));
                          setPromoApplied(null);
                        }}
                        disabled={quantity <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="font-semibold w-8 text-center">{quantity}</span>
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => {
                          setQuantity(Math.min(selectedTier.quantity - (selectedTier.sold || 0), quantity + 1));
                          setPromoApplied(null);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Promo Code */}
                  {!showPayPal && (
                    <div className="pt-2">
                      <label className="text-sm text-muted-foreground mb-2 block">Have a promo code?</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder="Enter code"
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value.toUpperCase());
                              setPromoApplied(null);
                            }}
                            className="pl-10 uppercase"
                            disabled={!!promoApplied}
                            data-testid="promo-code-input"
                          />
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={validatePromoCode}
                          disabled={!promoCode.trim() || validatingPromo || !!promoApplied}
                        >
                          {validatingPromo ? 'Checking...' : promoApplied ? 'Applied!' : 'Apply'}
                        </Button>
                      </div>
                      {promoApplied && (
                        <p className="text-sm text-green-400 mt-1">
                          {promoApplied.discount_type === 'percentage' 
                            ? `${promoApplied.discount_value}% off applied!` 
                            : `$${promoApplied.discount_value} off applied!`}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Subtotal</span>
                      <span>${(selectedTier.price * quantity).toFixed(2)}</span>
                    </div>
                    {promoApplied && (
                      <div className="flex items-center justify-between text-sm text-green-400">
                        <span>Discount</span>
                        <span>-${promoApplied.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Total</span>
                      <span className="font-heading font-bold text-xl text-primary">
                        ${promoApplied ? promoApplied.new_total.toFixed(2) : (selectedTier.price * quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Section */}
              {showBidPayPal && selectedTier.sale_mode === 'auction' ? (
                <div className="pt-4 border-t border-white/10">
                  <BidPayPalCheckout
                    orderId={pendingBidOrderId}
                    bidAmount={parseFloat(bidAmount)}
                    tierName={selectedTier.name}
                    eventTitle={event?.title}
                    onApprove={handleBidPayPalApprove}
                    onCancel={() => {
                      setShowBidPayPal(false);
                      setPendingBidOrderId(null);
                      setPendingBidId(null);
                    }}
                    onError={handleBidPayPalError}
                  />
                  <Button 
                    variant="ghost" 
                    className="w-full mt-3"
                    onClick={() => {
                      setShowBidPayPal(false);
                      setPendingBidOrderId(null);
                      setPendingBidId(null);
                    }}
                  >
                    Cancel bid
                  </Button>
                </div>
              ) : showPayPal && selectedTier.sale_mode !== 'auction' ? (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-muted-foreground mb-4 text-center">Complete your purchase with PayPal</p>
                  <PayPalCheckout
                    eventId={eventId}
                    tierName={selectedTier.name}
                    quantity={quantity}
                    promoCode={promoApplied ? promoCode : null}
                    totalAmount={promoApplied ? promoApplied.new_total : selectedTier.price * quantity}
                    onSuccess={handlePayPalSuccess}
                    onCancel={handlePayPalCancel}
                    onError={handlePayPalError}
                  />
                  <Button 
                    variant="ghost" 
                    className="w-full mt-3"
                    onClick={() => setShowPayPal(false)}
                  >
                    Back to order details
                  </Button>
                </div>
              ) : (
                <Button 
                  className="w-full gap-2" 
                  onClick={handlePurchase}
                  disabled={purchasing}
                  data-testid="confirm-purchase-btn"
                >
                  {purchasing ? (
                    'Processing...'
                  ) : selectedTier.sale_mode === 'auction' ? (
                    <>
                      <Gavel className="w-4 h-4" />
                      Place Bid with PayPal
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Pay with PayPal
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
